import { buildKeyItemEntries } from "./key-items";
import type { KeyItem } from "./schema/key-items";
import type { Morph } from "./schema/morphs";
import type { Race } from "./schema/races";
import type { Spell } from "./schema/spells";
import type { SpoilerDetail } from "./schema/spoiler-details";
import { chapterNumber } from "../lib/timeline/book/ChapterId";

export type CompendiumKind = "race" | "vehicle" | "organization" | "business" | "spell" | "morph" | "item";

/** Someone an entry belongs to: who has a morph, knows a spell, or held an item. */
export interface CompendiumPerson {
  characterId: string;
  displayName: string;
  /** The first chapter that shows it, or null when the story has not said. */
  chapterId: string | null;
  note?: string;
}

/** One thing in a series' compendium, ready for its page and for links from text. */
export interface CompendiumEntry {
  id: string;
  kind: CompendiumKind;
  name: string;
  aliases: string[];
  /** Ordinary words, so "tigers" links too. A name like Homer has to match exactly. */
  looseCase: boolean;
  description?: string;
  namedBy?: string;
  firstChapter?: string;
  chapterIds?: string[];
  fate?: { label: string; chapterId: string };
  people: CompendiumPerson[];
  imageStem: string;
  imageCredit?: string;
  /** Facts that give something away, kept closed on the page. */
  details?: SpoilerDetail[];
}

export interface CompendiumSources {
  races: readonly Race[];
  spells: readonly Spell[];
  morphs: readonly Morph[];
  items: readonly KeyItem[];
  nameOf: (characterId: string) => string;
}

/** "Wingardium Leviosa" to "wingardium-leviosa", and "Sorcerer's" to "sorcerers". */
export function compendiumSlug(name: string): string {
  return name.toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const earliest = (chapterIds: readonly (string | null)[]): string | undefined => chapterIds
  .filter((id): id is string => id !== null)
  .sort((a, b) => (chapterNumber(a) ?? 0) - (chapterNumber(b) ?? 0))[0];

const raceEntry = (race: Race): CompendiumEntry => ({
  id: race.id,
  kind: race.kind,
  name: race.name,
  aliases: race.aliases,
  looseCase: race.kind === "vehicle",
  description: race.description,
  namedBy: race.namedBy,
  firstChapter: race.firstChapter,
  ...(race.details === undefined ? {} : { details: race.details }),
  people: [],
  imageStem: `${race.kind}-${race.id}`,
});

function spellEntry(spell: Spell, nameOf: CompendiumSources["nameOf"]): CompendiumEntry {
  const id = compendiumSlug(spell.name);
  const firstChapter = earliest(spell.knownBy.map((person) => person.chapterId));
  return {
    id,
    kind: "spell",
    name: spell.name,
    aliases: spell.aliases ?? [],
    looseCase: false,
    description: spell.description,
    ...(spell.details === undefined ? {} : { details: spell.details }),
    ...(firstChapter === undefined ? {} : { firstChapter }),
    people: spell.knownBy.map(({ characterId, chapterId }) => ({
      characterId,
      displayName: nameOf(characterId),
      chapterId,
    })),
    imageStem: `spell-${id}`,
  };
}

/** A look named for part of the morph's name means the name is the animal, not a pet's name. */
function animalWords(morph: Morph): string[] | null {
  const slug = compendiumSlug(morph.name);
  if (morph.lookId === slug) {
    return [];
  }
  return slug.split("-").includes(morph.lookId) ? [morph.lookId] : null;
}

/** One entry per morph name, so a form two people share has one page listing both. */
function morphEntries(morphs: readonly Morph[], nameOf: CompendiumSources["nameOf"]): CompendiumEntry[] {
  const groups = new Map<string, Morph[]>();
  for (const morph of morphs.filter((item) => item.kind !== "variation")) {
    const id = compendiumSlug(morph.name);
    groups.set(id, [...(groups.get(id) ?? []), morph]);
  }
  return [...groups].map(([id, group]) => {
    const first = group[0]!;
    const words = animalWords(first);
    const credit = group.find((morph) => morph.imageCredit !== undefined)?.imageCredit;
    const firstChapter = earliest(group.map((morph) => morph.acquired));
    const about = group.find((morph) => morph.about !== undefined)?.about;
    const details = group.flatMap((morph) => morph.details ?? []);
    return {
      id,
      kind: "morph",
      name: first.name,
      aliases: [...new Set([...group.flatMap((morph) => morph.aliases ?? []), ...(words ?? [])])],
      looseCase: words !== null,
      ...(about === undefined ? {} : { description: about }),
      ...(details.length === 0 ? {} : { details }),
      ...(firstChapter === undefined ? {} : { firstChapter }),
      people: group.map((morph) => ({
        characterId: morph.characterId,
        displayName: nameOf(morph.characterId),
        chapterId: morph.acquired,
        note: morph.description,
      })),
      imageStem: `${first.characterId}--${first.lookId}`,
      ...(credit === undefined ? {} : { imageCredit: credit }),
    };
  });
}

function itemEntries(items: readonly KeyItem[], nameOf: CompendiumSources["nameOf"]): CompendiumEntry[] {
  const built = buildKeyItemEntries(items, nameOf);
  return items.map((item, index) => {
    const entry = built[index]!;
    const firstChapter = item.firstChapter ?? entry.chapterIds[0];
    return {
      id: item.id,
      kind: "item",
      name: item.name,
      aliases: item.aliases ?? [],
      looseCase: true,
      ...(item.description === undefined ? {} : { description: item.description }),
      ...(firstChapter === undefined ? {} : { firstChapter }),
      chapterIds: entry.chapterIds,
      ...(item.details === undefined ? {} : { details: item.details }),
      ...(entry.fate === undefined ? {} : { fate: entry.fate }),
      people: entry.holders.map((holder) => ({
        characterId: holder.characterId,
        displayName: holder.displayName,
        chapterId: holder.chapterIds[0] ?? null,
      })),
      imageStem: `item-${item.id}`,
    };
  });
}

/** Everything a book's compendium holds. A kind a book does not have is simply absent. */
export function buildCompendium(sources: CompendiumSources): CompendiumEntry[] {
  return [
    ...sources.races.map(raceEntry),
    ...sources.spells.map((spell) => spellEntry(spell, sources.nameOf)),
    ...morphEntries(sources.morphs, sources.nameOf),
    ...itemEntries(sources.items, sources.nameOf),
  ];
}
